import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { hasEntityBankAccounts, hasEntityCards, hasEntityInvestorProfile, hasEntityUsLegalStatus } from '@/entities/utils/has-entity';
import useDocuments from '@/entity/hooks/use-documents';
import useEntityOwnedBusinesses from '@/entity/hooks/use-entity-owned-businesses';
import AddressFieldset from '../address-fieldset';
import CustomDataFields from '../custom-data-fields';
import DocumentFieldset from '../document-fieldset';
import Fieldset from '../fieldset';
import GridLayout, { type Section, SectionKeys } from '../grid-layout';
import FinancialFieldset from './components/financial-fieldset';
import InvestorProfileFields from './components/investor-profile-fields';
import OwnedBusinesses from './components/owned-businesses';
import useFieldsets from './hooks/use-fieldsets';
type PersonVaultProps = {
  entity: Entity;
};
const PersonVault = t0 => {
  const $ = _c(51);
  const {
    entity
  } = t0;
  let t1;
  if ($[0] !== entity) {
    t1 = hasEntityCards(entity);
    $[0] = entity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasCards = t1;
  let t2;
  if ($[2] !== entity) {
    t2 = hasEntityBankAccounts(entity);
    $[2] = entity;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasBankAccounts = t2;
  let t3;
  if ($[4] !== entity) {
    t3 = hasEntityInvestorProfile(entity);
    $[4] = entity;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasInvestorProfile = t3;
  let t4;
  if ($[6] !== entity) {
    t4 = hasEntityUsLegalStatus(entity);
    $[6] = entity;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const hasUsLegalStatus = t4;
  const {
    data: documents
  } = useDocuments(entity.id);
  const hasDocuments = documents && documents.length > 0;
  let t5;
  if ($[8] !== entity.data) {
    t5 = entity.data.some(_temp);
    $[8] = entity.data;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const hasCustomData = t5;
  const {
    basic,
    address,
    usLegalStatus,
    identity,
    investorProfile,
    cards,
    custom
  } = useFieldsets(!hasUsLegalStatus);
  const {
    ownedBusinesses,
    hasBusinesses
  } = useEntityOwnedBusinesses(entity.id);
  const hasFinancial = hasCards || hasBankAccounts;
  let t6;
  if ($[10] !== basic.fields || $[11] !== basic.iconComponent || $[12] !== basic.title) {
    t6 = {
      key: SectionKeys.BASIC,
      content: <Fieldset fields={basic.fields} iconComponent={basic.iconComponent} title={basic.title} />
    };
    $[10] = basic.fields;
    $[11] = basic.iconComponent;
    $[12] = basic.title;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== identity.fields || $[15] !== identity.iconComponent || $[16] !== identity.title) {
    t7 = {
      key: SectionKeys.IDENTITY,
      content: <Fieldset fields={identity.fields} iconComponent={identity.iconComponent} title={identity.title} />
    };
    $[14] = identity.fields;
    $[15] = identity.iconComponent;
    $[16] = identity.title;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== address.fields || $[19] !== address.iconComponent || $[20] !== address.title) {
    t8 = {
      key: SectionKeys.ADDRESS,
      content: <AddressFieldset fields={address.fields} iconComponent={address.iconComponent} title={address.title} />
    };
    $[18] = address.fields;
    $[19] = address.iconComponent;
    $[20] = address.title;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== hasUsLegalStatus || $[23] !== usLegalStatus) {
    t9 = hasUsLegalStatus && {
      key: SectionKeys.US_LEGAL,
      content: <Fieldset fields={usLegalStatus.fields} iconComponent={usLegalStatus.iconComponent} title={usLegalStatus.title} />
    };
    $[22] = hasUsLegalStatus;
    $[23] = usLegalStatus;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== documents || $[26] !== hasDocuments) {
    t10 = hasDocuments && {
      key: SectionKeys.DOCUMENTS,
      content: <DocumentFieldset documents={documents} />
    };
    $[25] = documents;
    $[26] = hasDocuments;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== cards || $[29] !== hasFinancial) {
    t11 = hasFinancial && {
      key: SectionKeys.FINANCIAL,
      content: <FinancialFieldset title={cards.title} iconComponent={cards.iconComponent} />
    };
    $[28] = cards;
    $[29] = hasFinancial;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== hasBusinesses || $[32] !== ownedBusinesses) {
    t12 = hasBusinesses && {
      key: SectionKeys.BUSINESS_OWNERS,
      content: <OwnedBusinesses businesses={ownedBusinesses} />
    };
    $[31] = hasBusinesses;
    $[32] = ownedBusinesses;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== custom || $[35] !== entity || $[36] !== hasCustomData) {
    t13 = hasCustomData && {
      key: SectionKeys.CUSTOM,
      content: <CustomDataFields entity={entity} title={custom.title} iconComponent={custom.iconComponent} />
    };
    $[34] = custom;
    $[35] = entity;
    $[36] = hasCustomData;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== hasInvestorProfile || $[39] !== investorProfile) {
    t14 = hasInvestorProfile && {
      key: SectionKeys.INVESTOR,
      content: <Fieldset fields={investorProfile.fields} iconComponent={investorProfile.iconComponent} title={investorProfile.title}><InvestorProfileFields /></Fieldset>
    };
    $[38] = hasInvestorProfile;
    $[39] = investorProfile;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t6 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    const sections = [t6, t7, t8, t9, t10, t11, t12, t13, t14].filter(_temp2);
    t15 = <GridLayout sections={sections} />;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t6;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  return t15;
};
export default PersonVault;
function _temp(attr) {
  return attr.identifier.startsWith("custom");
}
function _temp2(section) {
  return Boolean(section);
}